import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useFloating } from '@/shared/hooks/useFloating';

const fadeIn = keyframes`from{opacity:0;transform:translateY(-4px)}to{opacity:1;transform:translateY(0)}`;

const Wrap = styled.div`display:inline-flex;flex-direction:column;width:100%;`;

const InputRow = styled.div`
  display:flex;align-items:center;width:100%;height:34px;
  border-radius:7px;border:1px solid var(--border-subtle);
  background:var(--bg-primary);transition:border-color 0.12s ease;
  &:focus-within{border-color:var(--accent);}
  &:hover{border-color:var(--accent);}
`;

const ManualInput = styled.input`
  flex:1;height:100%;border:none;background:transparent;
  color:var(--text-primary);font-size:13px;font-family:inherit;
  padding:0 0 0 10px;outline:none;min-width:0;
  &::placeholder{color:var(--text-tertiary);}
`;

const IconBtn = styled.button`
  width:32px;height:32px;border:none;background:transparent;
  color:var(--text-tertiary);cursor:pointer;display:flex;align-items:center;
  justify-content:center;border-radius:0 6px 6px 0;flex-shrink:0;
  transition:all 0.08s ease;
  &:hover{color:var(--accent);background:var(--bg-hover);}
`;

const ClearBtn = styled.button`
  width:20px;height:20px;border-radius:4px;border:none;
  background:transparent;color:var(--text-tertiary);cursor:pointer;
  display:flex;align-items:center;justify-content:center;flex-shrink:0;
  &:hover{background:var(--bg-hover);color:var(--text-primary);}
`;

const Panel = styled.div<{ $x: number; $y: number; $dir: 'up' | 'down' }>`
  position:absolute;z-index:9999;
  left:${({ $x }) => $x}px;
  top:${({ $dir, $y }) => $dir === 'down' ? `${$y + 4}px` : 'auto'};
  bottom:${({ $dir, $y }) => $dir === 'up' ? `${window.innerHeight - $y + 4}px` : 'auto'};
  background:var(--bg-surface);border:1px solid var(--border-subtle);border-radius:10px;
  box-shadow:0 12px 40px rgba(0,0,0,0.22);padding:12px;width:280px;
  animation:${fadeIn} 0.12s ease;
`;

const NavRow = styled.div`display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;`;
const NavBtn = styled.button`
  width:28px;height:28px;border-radius:6px;border:none;background:transparent;
  color:var(--text-secondary);cursor:pointer;display:flex;align-items:center;justify-content:center;
  transition:all 0.08s ease;&:hover{background:var(--bg-hover);color:var(--text-primary);}
`;
const MonthLabel = styled.span`font-size:13px;font-weight:600;color:var(--text-primary);`;

const DaysGrid = styled.div`display:grid;grid-template-columns:repeat(7,1fr);gap:2px;`;
const DayHead = styled.span`font-size:10px;font-weight:600;color:var(--text-tertiary);text-align:center;padding:4px 0;text-transform:uppercase;`;
const DayCell = styled.button<{ $today?: boolean; $selected?: boolean; $outside?: boolean; $disabled?: boolean }>`
  width:34px;height:34px;border-radius:7px;border:none;
  font-size:12px;font-weight:${({ $selected, $today }) => ($selected || $today ? 600 : 400)};
  cursor:${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  display:flex;align-items:center;justify-content:center;font-family:inherit;
  transition:all 0.08s ease;
  background:${({ $selected }) => ($selected ? 'var(--accent)' : 'transparent')};
  color:${({ $selected, $outside, $disabled }) =>
    $selected ? 'var(--accent-contrast)' : $outside || $disabled ? 'var(--text-tertiary)' : 'var(--text-primary)'};
  opacity:${({ $outside }) => ($outside ? 0.35 : 1)};
  ${({ $today, $selected }) => $today && !$selected ? 'box-shadow:inset 0 0 0 1.5px var(--accent);' : ''}
  &:hover:not(:disabled){
    background:${({ $selected }) => ($selected ? 'var(--accent-hover)' : 'var(--bg-hover)')};
  }
`;

const QuickRow = styled.div`
  display:flex;gap:4px;margin-top:8px;padding-top:8px;border-top:1px solid var(--border-subtle);flex-wrap:wrap;
`;
const QuickBtn = styled.button`
  padding:4px 8px;border-radius:5px;border:1px solid var(--border-subtle);
  background:transparent;color:var(--text-secondary);font-size:11px;font-weight:500;
  cursor:pointer;font-family:inherit;transition:all 0.08s ease;
  &:hover{background:var(--bg-hover);color:var(--text-primary);border-color:var(--accent);}
`;

const DAYS_RU = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS_RU = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

const toDateStr = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseDate = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };

const getCalendarDays = (year: number, month: number) => {
  const first = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0).getDate();
  let startDow = first.getDay();
  if (startDow === 0) startDow = 7;
  startDow -= 1;
  const days: Array<{ date: Date; outside: boolean }> = [];
  for (let i = startDow - 1; i >= 0; i--) days.push({ date: new Date(year, month, -i), outside: true });
  for (let i = 1; i <= lastDay; i++) days.push({ date: new Date(year, month, i), outside: false });
  while (days.length < 42) days.push({ date: new Date(year, month + 1, days.length - startDow - lastDay + 1), outside: true });
  return days;
};

const fmtDisplay = (value: string) => {
  if (!value) return '';
  try {
    const d = parseDate(value);
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return value; }
};

const tryParseInput = (raw: string): string | null => {
  const clean = raw.trim();
  let m = clean.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (m) {
    const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
    if (!isNaN(d.getTime())) return toDateStr(d);
  }
  m = clean.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (!isNaN(d.getTime())) return toDateStr(d);
  }
  return null;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  clearable?: boolean;
  minDate?: string;
  maxDate?: string;
};

export const DatePicker: React.FC<Props> = ({ value, onChange, placeholder = 'дд.мм.гггг', clearable = true, minDate, maxDate }) => {
  const [open, setOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const { anchorRef, pos } = useFloating(open, 380, 280);
  const panelRef = useRef<HTMLDivElement>(null);
  const today = useMemo(() => new Date(), []);
  const todayStr = useMemo(() => toDateStr(today), [today]);

  // Memoised on `value`: as a bare expression this produced a new Date on every
  // render, and it is a dependency of the effect that snaps the calendar back to
  // the selected month — so paging to another month re-rendered, re-ran the
  // effect and immediately jumped back. The month appeared to flicker and never
  // changed, but only when a date was actually set.
  const selectedDate = useMemo(() => (value ? parseDate(value) : null), [value]);
  const [viewYear, setViewYear] = useState(selectedDate?.getFullYear() ?? today.getFullYear());
  const [viewMonth, setViewMonth] = useState(selectedDate?.getMonth() ?? today.getMonth());

  useEffect(() => { setInputText(value ? fmtDisplay(value) : ''); }, [value]);

  useEffect(() => {
    if (!open) return;
    if (selectedDate) { setViewYear(selectedDate.getFullYear()); setViewMonth(selectedDate.getMonth()); }
  }, [open, selectedDate]);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (anchorRef.current?.contains(e.target as Node)) return;
      if (panelRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [open, anchorRef]);

  const days = useMemo(() => getCalendarDays(viewYear, viewMonth), [viewYear, viewMonth]);

  const prevMonth = useCallback(() => {
    setViewMonth((m) => { if (m === 0) { setViewYear((y) => y - 1); return 11; } return m - 1; });
  }, []);
  const nextMonth = useCallback(() => {
    setViewMonth((m) => { if (m === 11) { setViewYear((y) => y + 1); return 0; } return m + 1; });
  }, []);

  const isDisabled = useCallback((dateStr: string) => {
    if (minDate && dateStr < minDate) return true;
    if (maxDate && dateStr > maxDate) return true;
    return false;
  }, [minDate, maxDate]);

  const selectDate = useCallback((d: Date) => {
    const str = toDateStr(d);
    if (isDisabled(str)) return;
    onChange(str);
    setOpen(false);
  }, [onChange, isDisabled]);

  const quickSet = useCallback((daysFromNow: number) => {
    const d = new Date();
    d.setDate(d.getDate() + daysFromNow);
    selectDate(d);
  }, [selectDate]);

  const commitInput = useCallback(() => {
    const clean = inputText.trim();
    if (!clean) { onChange(''); return; }
    const parsed = tryParseInput(clean);
    if (parsed && !isDisabled(parsed)) onChange(parsed);
    else setInputText(value ? fmtDisplay(value) : '');
  }, [inputText, onChange, value, isDisabled]);

  const panel = open ? createPortal(
    <Panel ref={panelRef} $x={pos.left} $y={pos.top} $dir={pos.direction}>
      <NavRow>
        <NavBtn type="button" onClick={prevMonth}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M10 3L5 8l5 5" /></svg>
        </NavBtn>
        <MonthLabel>{MONTHS_RU[viewMonth]} {viewYear}</MonthLabel>
        <NavBtn type="button" onClick={nextMonth}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 3l5 5-5 5" /></svg>
        </NavBtn>
      </NavRow>
      <DaysGrid>
        {DAYS_RU.map((d) => <DayHead key={d}>{d}</DayHead>)}
        {days.map((cell, i) => {
          const ds = toDateStr(cell.date);
          const dis = isDisabled(ds);
          return (
            <DayCell key={i} type="button" $today={ds === todayStr} $selected={ds === value} $outside={cell.outside} $disabled={dis} disabled={dis} onClick={() => selectDate(cell.date)}>
              {cell.date.getDate()}
            </DayCell>
          );
        })}
      </DaysGrid>
      <QuickRow>
        <QuickBtn type="button" onClick={() => quickSet(0)}>Сегодня</QuickBtn>
        <QuickBtn type="button" onClick={() => quickSet(1)}>Завтра</QuickBtn>
        <QuickBtn type="button" onClick={() => quickSet(7)}>Через неделю</QuickBtn>
        <QuickBtn type="button" onClick={() => quickSet(30)}>Через месяц</QuickBtn>
      </QuickRow>
    </Panel>,
    document.body,
  ) : null;

  return (
    <Wrap ref={anchorRef}>
      <InputRow>
        <ManualInput
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onBlur={commitInput}
          onKeyDown={(e) => { if (e.key === 'Enter') { commitInput(); (e.target as HTMLInputElement).blur(); } }}
          placeholder={placeholder}
        />
        {clearable && value && (
          <ClearBtn type="button" onClick={() => onChange('')} title="Очистить">
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 3l10 10M13 3L3 13" /></svg>
          </ClearBtn>
        )}
        <IconBtn type="button" onClick={() => setOpen((o) => !o)} title="Календарь">
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
            <rect x="2" y="3" width="12" height="11" rx="2" /><path d="M2 7h12M5 1v3M11 1v3" />
          </svg>
        </IconBtn>
      </InputRow>
      {panel}
    </Wrap>
  );
};
